import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
  Text,
  TextInput,
} from '@razorpay/blade/components';

import type { LeaveType } from 'api/types/attendanceSettingsTypes';

interface AddLeaveTypeModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAdd: (row: LeaveType) => void;
  initialData?: LeaveType | null;
}

export const AddLeaveTypeModal = ({
  isOpen,
  onDismiss,
  onAdd,
  initialData,
}: AddLeaveTypeModalProps) => {
  const [name, setName] = useState('');
  const [defaultLeaves, setDefaultLeaves] = useState('0');
  const [monthlyIncrement, setMonthlyIncrement] = useState('0');
  const [maxLeaves, setMaxLeaves] = useState('');
  const [carryForward, setCarryForward] = useState('');
  const [includeWeekends, setIncludeWeekends] = useState(false);

  const isEditMode = initialData != null;

  useEffect(() => {
    if (isOpen && initialData) {
      setName(initialData.name);
      setDefaultLeaves(String(initialData.defaultLeaves));
      setMonthlyIncrement(String(initialData.monthlyIncrement));
      setMaxLeaves(initialData.maxLeaves == null ? '' : String(initialData.maxLeaves));
      setCarryForward(initialData.carryForward == null ? '' : String(initialData.carryForward));
      setIncludeWeekends(initialData.includeWeekendsAndHolidays);
    } else if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const canSubmit = name.trim() !== '';

  const resetForm = () => {
    setName('');
    setDefaultLeaves('0');
    setMonthlyIncrement('0');
    setMaxLeaves('');
    setCarryForward('');
    setIncludeWeekends(false);
  };

  const handleDismiss = () => {
    resetForm();
    onDismiss();
  };

  const toNumber = (raw: string, allowNull = false): number | null => {
    if (!raw) return allowNull ? null : 0;
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return allowNull ? null : 0;
    return parsed;
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    onAdd({
      name: name.trim(),
      defaultLeaves: toNumber(defaultLeaves) ?? 0,
      monthlyIncrement: toNumber(monthlyIncrement) ?? 0,
      maxLeaves: toNumber(maxLeaves, true),
      carryForward: toNumber(carryForward, true),
      includeWeekendsAndHolidays: includeWeekends,
    });

    resetForm();
  };

  return (
    <Modal isOpen={isOpen} onDismiss={handleDismiss} size="small">
      <ModalHeader title={isEditMode ? 'Edit leave type' : 'Add leave type'} />
      <ModalBody>
        <Box display="flex" flexDirection="column" gap="spacing.6">
          <TextInput
            label="Leave type name"
            placeholder="e.g. Casual leave"
            value={name}
            onChange={({ value }) => setName(value ?? '')}
            necessityIndicator="required"
          />
          <Box display="flex" gap="spacing.4">
            <Box flex="1">
              <TextInput
                label="Default balance"
                type="number"
                value={defaultLeaves}
                onChange={({ value }) => setDefaultLeaves(value ?? '0')}
              />
            </Box>
            <Box flex="1">
              <TextInput
                label="Monthly increment"
                type="number"
                value={monthlyIncrement}
                onChange={({ value }) => setMonthlyIncrement(value ?? '0')}
              />
            </Box>
          </Box>
          <Box display="flex" gap="spacing.4">
            <Box flex="1">
              <TextInput
                label="Max balance"
                type="number"
                placeholder="No limit"
                value={maxLeaves}
                onChange={({ value }) => setMaxLeaves(value ?? '')}
              />
            </Box>
            <Box flex="1">
              <TextInput
                label="Carry forward"
                type="number"
                placeholder="None"
                value={carryForward}
                onChange={({ value }) => setCarryForward(value ?? '')}
              />
            </Box>
          </Box>
          <Box
            as="label"
            display="flex"
            alignItems="center"
            gap="spacing.4"
            marginTop="spacing.3"
            paddingBottom="spacing.4">
            <Switch
              size="medium"
              accessibilityLabel="Include weekends and holidays"
              isChecked={includeWeekends}
              onChange={({ isChecked }) => setIncludeWeekends(isChecked)}
            />
            <Text size="medium" color="surface.text.gray.subtle">
              Include weekends & holidays
            </Text>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.4">
          <Button variant="secondary" onClick={handleDismiss}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isDisabled={!canSubmit}>
            {isEditMode ? 'Save Changes' : 'Add Leave Type'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
